import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'

interface Props {
  materialAnomalies?: string
  forkliftDriverNote?: string
  note?: string
  truckAnomalies?: string
}

const hasText = (value?: string): value is string => value != null && value.trim().length > 0

const normalizeNewlines = (value?: string): string => (value ?? '').replace(/\r\n|\r|\n/g, ', ')

function NoteCell(props: Props) {
  const { note, forkliftDriverNote, truckAnomalies, materialAnomalies } = props
  const { t } = useTranslation()

  const { officeNote, rescheduleNotes } = useMemo(() => {
    const [first, ...rest] = note?.split('-').map(s => s.trim()) ?? []
    return { officeNote: first, rescheduleNotes: rest.filter(hasText) }
  }, [note])

  const tooltipHtml: JSX.Element[] = useMemo(() => {
    const sections: JSX.Element[] = []

    const officeParts: string[] = []
    if (hasText(officeNote)) {
      officeParts.push(`${t('mcs.cargo.note.label')}: ${officeNote}`)
    }
    officeParts.push(...rescheduleNotes)
    if (officeParts.length > 0) {
      sections.push(<div key="office">{officeParts.join(' - ')}</div>)
    }

    if (hasText(forkliftDriverNote) || hasText(truckAnomalies)) {
      sections.push(
        <div key="forklift">
          {t('mcs.cargo.forkliftDriverNote.label')}:{' '}
          {[normalizeNewlines(forkliftDriverNote), normalizeNewlines(truckAnomalies)].join(' ')}
        </div>
      )
    }

    if (hasText(materialAnomalies)) {
      sections.push(
        <div key="material">
          {t('mcs.cargo.materialAnomalies.label')}: {normalizeNewlines(materialAnomalies)}
        </div>
      )
    }

    return sections.reduce<JSX.Element[]>((acc, section, index) => {
      if (index > 0) {
        acc.push(<hr key={`sep-${index}`} />)
      }
      acc.push(section)
      return acc
    }, [])
  }, [officeNote, rescheduleNotes, forkliftDriverNote, truckAnomalies, materialAnomalies, t])

  if (tooltipHtml.length === 0) {
    return null
  }

  return (
    <Tooltip arrow={true} html={<>{tooltipHtml}</>}>
      {hasText(officeNote) && officeNote} <FontAwesomeIcon icon={faInfoCircle} className="text-secondary" />
    </Tooltip>
  )
}

export default NoteCell
